import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';
import type { selectRow, selectRowByRowIndex } from '@client-shared/ui-agGrid';
import { GridApi } from 'ag-grid-community';

import {
  GridMode,
  isEditingMode,
  useEditableGridLogic,
  UseEditableGridLogicParams,
} from '../../../shared/gridEdit/useEditableGridLogic';

import agGridConfig from '../../../agGridConfig';
import AgGrid from '../../../shared/agGrid/agGrid.component';
import Button from '../../../shared/button.component';
import Select from '../../../shared/reactSelect/select.component';
import ConfirmDialog from '../../../shared/confirmDialog.component';
import NavigationPrompt from '../../../shared/navigationPrompt.component';
import EditableGridButtonsComponent from '../../../shared/gridEdit/EditableGridButtons.component';

import { useDialogState } from '../../../shared/hooks/useDialogState';
import { YesNoFlag } from '../../../shared/types';
import { validateScenarioMember } from './validators';

import {
  ScenarioMember,
  ScenariosMembersListLookupData,
  useScenarioMetadata,
  useScenariosMembersList,
  useScenariosMembersListLookupData,
} from '@queries/scenarios';

import {
  useAddScenarioMember,
  useAssignMembersToScenario,
  useDeleteScenarioMember,
  useDisassociateMembersFromScenario,
  useEditScenarioMember,
} from '../../../shared/mutations/scenarios';

import DeleteScenarioMemberModal from './DeleteScenarioMemberModal.component';
import { DeleteModalData } from './types';
import { getColumnDefinitions } from './getColumnDefinitions';
import { useSelectableRowsGridLogic } from '@client-shared/agGrid/selectableRows';
import AlertWarning from '../../../shared/alertWarning.component';
import { MAX_NUMBER_OF_MEMBERS_PER_SCENARIO } from './constants';
import { useSelectCompanyCodeByCustomGroup } from './useSelectCustomGroupsCompanyCodes.hook';


// workaround for a problem with incorrect calculation of membersIncludedInScenario on first render
const useCalculateRowNumberInGrid = (): [{ current: GridApi | null}, number, () => void] => {
  const gridApiRef = useRef<GridApi | null>(null);
  const [rowNumber, setRowNumber] = useState(0);

  const calculateRowNumber = useCallback(() => {
    setRowNumber(gridApiRef.current?.getDisplayedRowCount() || 0);
  }, []);

  return [
    gridApiRef,
    rowNumber,
    calculateRowNumber,
  ];
};

type ScenarioManageMembersGridProps = {
  clientId: number,
  scenarioId: number,
  selectRow: typeof selectRow,
  selectRowByRowIndex: typeof selectRowByRowIndex,
  deleteModalData: DeleteModalData<ScenarioMember>
  hideScenarioMemberModalForDelete: () => void,
  navigateToScenario: () => void,
}

const createScenarioMemberRowFactory = ({ periodName }: { periodName: string }) => (): ScenarioMember => ({
  periodName,
  businessUnit: '',
  companyCode: '',
  companyCodeDescription: '',
  functionalCurrency: '',
  legalEntity: '',
  overrideTaxCurrency: '',
  recordFTC: YesNoFlag.Y,
  subledgerFlag: YesNoFlag.N,
  scenarioEnabled: YesNoFlag.Y,
  statCalc: YesNoFlag.N,
  overrideSecondaryTax: '',
});

type EditableGridParams = UseEditableGridLogicParams<ScenarioMember, ScenariosMembersListLookupData>;
type OnSaveCallback = EditableGridParams['onSave'];
type OnCancelCallback = EditableGridParams['onCancel'];


const ScenarioManageMembersGrid = ({
  clientId,
  scenarioId,
  selectRow,
  selectRowByRowIndex,
  deleteModalData,
  hideScenarioMemberModalForDelete,
}: ScenarioManageMembersGridProps) => {
  const [mode, setMode] = useState(GridMode.View);
  const [isResetting, setResetting] = useState(false);

  const {
    showDialog: showCancelDialog,
    hideDialog: hideCancelDialog,
    isDialogVisible: isCancelDialogVisible,
  } = useDialogState();

  const {
    data: scenarioMembers,
    isFetching: isFetchingScenarioMembers,
  } = useScenariosMembersList({ scenarioId });

  const {
    data: scenarioMetadata,
    isLoading: isLoadingScenarioMetadata,
  } = useScenarioMetadata({ scenarioId });

  const {
    lookUpData,
    isLoading: isLoadingLookupData,
  } = useScenariosMembersListLookupData({ periodName: scenarioMetadata?.periodName });

  const isScenarioLocked = scenarioMetadata?.lockedFlag === YesNoFlag.Y;

  const onSaveCallback = useCallback((__, { scenarioMember, resetEditableGrid }) => {
    setMode(GridMode.View);
    selectRow({
      name: agGridConfig.scenariosManageMembers.name,
      fields: agGridConfig.scenariosManageMembers.fields,
      data: scenarioMember,
    });
    resetEditableGrid();
  }, [setMode, selectRow]);

  const {
    mutate: addScenarioMember,
    isLoading: isAddingScenarioMember,
  } = useAddScenarioMember({ scenarioId }, onSaveCallback);

  const {
    mutate: editScenarioMember,
    isLoading: isEditingScenarioMember,
  } = useEditScenarioMember({ scenarioId }, onSaveCallback);

  const {
    mutate: assignToScenario,
    isLoading: isAssigningToScenario,
  } = useAssignMembersToScenario({ scenarioId });

  const {
    mutate: disassociateFromScenario,
    isLoading: isDisassociatingFromScenario,
  } = useDisassociateMembersFromScenario({ scenarioId });

  const afterDelete = useCallback(() => {
    if (deleteModalData?.data) {
      selectRowByRowIndex(deleteModalData.data.rowIndex === 0 ? 0 : deleteModalData.data.rowIndex - 1);
    }
    hideScenarioMemberModalForDelete();
  }, [hideScenarioMemberModalForDelete, selectRowByRowIndex, deleteModalData]);

  const {
    mutate: deleteScenarioMember,
    isLoading: isDeletingScenarioMember,
  } = useDeleteScenarioMember({ scenarioId }, afterDelete);

  const shouldShowNavigationPrompt = isEditingMode(mode);

  const createRow = useMemo(
    () => createScenarioMemberRowFactory({ periodName: scenarioMetadata?.periodName || '' }),
    [scenarioMetadata?.periodName]
  );

  const onSave = useCallback<OnSaveCallback>( async ({ hasErrors, addedRows, updatedRows, reset }) => {
    if (hasErrors) {
      return;
    }

    const [addedRow] = addedRows;
    const [editedRow] = updatedRows;

    if (mode === GridMode.Add && addedRow) {
      setMode(GridMode.View);
      addScenarioMember({
        scenarioMember: addedRow,
        resetEditableGrid: reset,
      });
      return;
    }

    if (mode === GridMode.Edit && editedRow) {
      setMode(GridMode.View);
      editScenarioMember({
        scenarioMember: editedRow,
        resetEditableGrid: reset,
      });
    }
  }, [addScenarioMember, editScenarioMember, mode, setMode]);

  const onCancel = useCallback<OnCancelCallback>((hasChanges, reset) => {
    if (hasChanges) {
      showCancelDialog();
    } else {
      setMode(GridMode.View);
      reset();
    }
  }, [setMode, showCancelDialog]);

  const onReset = useCallback(() => {
    setResetting(false);
  }, [setResetting]);


  const [
    gridApiRef,
    rowNumber,
    calculateRowNumber,
  ] = useCalculateRowNumberInGrid();

  const {
    save,
    cancel,
    reset,
    addRow,
    setRowData,
    setEditedRow,
    gridApi,
    editedRowId,
    onColumnEverythingChanged,
    ...gridProps
  } = useEditableGridLogic<ScenarioMember, ScenariosMembersListLookupData>({
    data: scenarioMembers as ScenarioMember[],
    onSave,
    onCancel,
    onReset,
    mode,
    createRow,
    validateRow: validateScenarioMember,
    getRowUniqueId: data => data.meta.isAdded ? null : data.companyCode,
    includeMetaWhenSaving: false,
    clientId,
    context: lookUpData,
    onColumnEverythingChanged: calculateRowNumber,
  });

  gridApiRef.current = gridApi;

  const selectionApi = useSelectableRowsGridLogic<ScenarioMember>({
    gridApi,
    getRowUniqueId: companyCode => companyCode.companyCode,
  });

  const editRow = useCallback(data => {
    setMode(GridMode.Edit);
    setEditedRow(data);
  }, [setEditedRow]);

  const onAddClick = useCallback(() => {
    setMode(GridMode.Add);
    addRow();
  }, [setMode, addRow]);

  const resetGrid = useCallback(() => {
    setResetting(true);
    reset();
    setMode(GridMode.View);
    selectionApi.resetSelection();
  }, [setResetting, reset, setMode, selectionApi]);

  const confirmCancel = useCallback(() => {
    setResetting(true);
    resetGrid();
    hideCancelDialog();
    setMode(GridMode.View);
  }, [resetGrid, hideCancelDialog, setMode, setResetting]);

  const getSelectedCompanyCodes = useCallback(
    () => selectionApi.getSelectedRows().map(({ companyCode }) => companyCode),
    [selectionApi]
  );

  const onAssignToScenarioClick = useCallback(() => {
    assignToScenario(getSelectedCompanyCodes());
    resetGrid();
  }, [assignToScenario, getSelectedCompanyCodes, resetGrid]);

  const onDisassociateFromScenarioClick = useCallback(() => {
    disassociateFromScenario(getSelectedCompanyCodes());
    resetGrid();
  }, [disassociateFromScenario, getSelectedCompanyCodes, resetGrid]);

  const columnDefinitions = useMemo(() => getColumnDefinitions({
    setRowData,
    editRow,
    lookUpData,
    context: {
      mode,
      editedRowId,
      selectionApi,
      scenarioDataSource: scenarioMetadata?.dataSource,
      isScenarioLocked,
    },
  }), [
    setRowData, editRow, lookUpData, mode, editedRowId, selectionApi, scenarioMetadata?.dataSource, isScenarioLocked,
  ]);

  useEffect(() => {
    setTimeout(() => {
      gridApi?.refreshCells({ force: true });
    }, 0);
  }, [isScenarioLocked, gridApi]);

  const isLoading = isFetchingScenarioMembers
    || isLoadingLookupData
    || isAddingScenarioMember
    || isEditingScenarioMember
    || isResetting
    || isAssigningToScenario
    || isDisassociatingFromScenario
    || isLoadingScenarioMetadata
    || isDeletingScenarioMember;

  const membersIncludedInScenario = useMemo(() => {
    let includedInScenario = 0;

    gridApi?.forEachNode(({ data }) => {
      const { scenarioEnabled } = data;

      if (scenarioEnabled === YesNoFlag.Y || selectionApi?.isSelected(data)) {
        includedInScenario++;
      }
    });

    return includedInScenario;
  }, [rowNumber, gridApi, selectionApi, scenarioMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectAllMembers = useCallback(
    () => selectionApi.selectFiltered(),
    [selectionApi]
  );

  const { dropdownProps: customGroupDropdownProps } = useSelectCompanyCodeByCustomGroup({
    periodName: scenarioMetadata?.periodName,
    selectionApi,
    gridApi,
    isScenarioLocked,
  });

  const unselectAllMembers = () => selectionApi.unselectFiltered();

  const areTooManyMembers = membersIncludedInScenario > MAX_NUMBER_OF_MEMBERS_PER_SCENARIO;

  const canAddCompanyCode = Boolean(
    lookUpData.businessUnitOptions.length
    && lookUpData.businessesOptions.length
    && lookUpData.connectionsOptions.length
    && lookUpData.countriesOptions.length
  );

  return (
    <div
      className={classNames(
        'ag-grid-full-page-with-quick-actions',
        areTooManyMembers && 'with-alert'
      )}
    >
      <AlertWarning
        warningMessage={`Scenarios are limited to ${MAX_NUMBER_OF_MEMBERS_PER_SCENARIO} members.`}
        show={areTooManyMembers}
      />
      <EditableGridButtonsComponent
        mode={mode}
        disabled={isLoading || isScenarioLocked}
        onAddClick={canAddCompanyCode && onAddClick}
        onCancelClick={cancel}
        onSaveClick={save}
        addButtonText={'Create a new member'}
        extraButtonOnLeft={
          <div className="quick-actions-widget">
            <label>
              {
                isScenarioLocked
                  ? 'Filter company codes by custom group'
                  : 'Filter and select company codes by custom group:'
              }
              <Select
                className="custom-group-dropdown"
                clearable
                {...customGroupDropdownProps}
              />
            </label>
          </div>
        }
      />
      <div className="">
        <AgGrid
          className="ag-theme-bootstrap"
          columnDefs={columnDefinitions}
          isGridLoading={isLoading}
          context={{
            ...lookUpData,
            editedRowId,
            mode,
            scenarioId,
          }}
          onColumnEverythingChanged={onColumnEverythingChanged}
          suppressRowClickSelection
          suppressScrollOnNewData
          enableBrowserTooltips
          stopEditingWhenCellsLoseFocus
          suppressCellFocus
          rowSelection="multiple"
          withSearchBar
          {...gridProps}
        />
      </div>
      <div className="d-flex">
        <div className="d-flex">
          <Button
            onClick={selectAllMembers}
            disabled={isLoading || isScenarioLocked}
            className="ml-2"
            kind="secondary"
          >
            Select all
          </Button>

          <Button
            onClick={unselectAllMembers} // eslint-disable-line react/jsx-no-bind
            disabled={isLoading || isScenarioLocked}
            className="ml-2"
            kind="secondary"
          >
            Clear all
          </Button>
        </div>
        <div className="ml-auto">
          <Button
            onClick={onAssignToScenarioClick}
            disabled={isLoading || !selectionApi.selectedRowsCount || areTooManyMembers || isScenarioLocked}
          >
            Add {selectionApi.selectedRowsCount} selected row(s) to scenario
          </Button>
          <Button
            onClick={onDisassociateFromScenarioClick}
            disabled={isLoading || !selectionApi.selectedRowsCount || isScenarioLocked}
            className={classNames('ml-2 ', { 'a-bg-red-light': !(isLoading || !selectionApi.selectedRowsCount) })}
          >
            Remove {selectionApi.selectedRowsCount} selected row(s) from scenario
          </Button>
        </div>
        <NavigationPrompt show={shouldShowNavigationPrompt} />
        <ReactTooltip place="right" effect="float" />
        <ConfirmDialog
          onCancel={hideCancelDialog}
          onConfirm={confirmCancel}
          message="Are you sure you want to discard the changes?"
          title="Confirm Cancel"
          dismissText="No"
          submitText="Yes"
          isVisible={isCancelDialogVisible}
        />
        <DeleteScenarioMemberModal
          entityToRemove={deleteModalData?.data?.entityToRemove}
          rowIndex={deleteModalData?.data?.rowIndex}
          isVisible={deleteModalData.show}
          hideModal={hideScenarioMemberModalForDelete}
          remove={deleteScenarioMember}
          isLoading={isDeletingScenarioMember}
        />
      </div>
    </div>
  );
};


export default ScenarioManageMembersGrid;
